/**
 * Mobile Bottom Navigation - Elite Glass Blur Nav Bar
 * 
 * MOBILE ONLY - Hidden on desktop via CSS
 * Features:
 * - Glass-blur background for native app feel
 * - Elevated center "Vibe" button
 * - Hide on scroll down, show on scroll up
 * - Safe area inset support for notched devices
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Ticket, User, Home, Music } from 'lucide-react';

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Hide on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);
  
  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };
  
  const navItems = [
    {
      id: 'home',
      label: 'Home',
      icon: Home,
      path: '/',
    },
    {
      id: 'search',
      label: 'Explore',
      icon: Search,
      path: '/experiences',
    },
    {
      id: 'vibe',
      label: 'Vibe',
      icon: Music,
      path: null,
      isCenter: true,
    },
    {
      id: 'trips',
      label: 'Trips',
      icon: Ticket,
      path: '/bookings',
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: User,
      path: '/profile',
    },
  ];
  
  const handleVibeClick = () => {
    // Dispatch custom event to trigger audio playback
    const event = new CustomEvent("playVibe");
    window.dispatchEvent(event);
  };
  
  return (
    <>
      {/* Spacer to prevent content being hidden behind nav */}
      <div className="h-20 md:hidden" aria-hidden="true" />
      
      {/* Bottom Nav */}
      <nav 
        id="mobile-bottom-nav" 
        data-testid="mobile-bottom-nav"
        className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-transform duration-300 ease-out ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
      >
        {/* Glass Blur Background */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'rgba(8, 11, 16, 0.85)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        />
        
        {/* Nav Items Container */}
        <div className="relative flex items-end justify-around px-1 py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            // Center Vibe Button - Elevated
            if (item.isCenter) {
              return (
                <button
                  key={item.id}
                  onClick={handleVibeClick}
                  className="relative -mt-4 flex flex-col items-center focus:outline-none"
                  data-testid={`mobile-nav-${item.id}`}
                  aria-label={item.label}
                >
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-transform active:scale-95"
                    style={{
                      background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
                      boxShadow: '0 4px 20px rgba(220, 38, 38, 0.5), inset 0 1px 0 rgba(255,255,255,0.2)'
                    }}
                  >
                    <Icon className="w-6 h-6 text-white" strokeWidth={2.5} />
                  </div>
                  <span className="text-[10px] text-primary mt-1 font-semibold tracking-wide">
                    {item.label}
                  </span>
                </button>
              );
            }
            
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center py-2 px-3 min-w-[56px] transition-all focus:outline-none ${
                  active ? '' : ''
                }`}
                data-testid={`mobile-nav-${item.id}`}
                aria-label={item.label}
              >
                <div className={`p-2 rounded-xl transition-all duration-200 ${
                  active 
                    ? 'bg-primary/15' 
                    : 'bg-transparent'
                }`}>
                  <Icon 
                    className={`w-5 h-5 transition-colors duration-200 ${
                      active ? 'text-primary' : 'text-muted-foreground'
                    }`}
                    strokeWidth={active ? 2.5 : 2}
                  />
                </div>
                <span className={`text-[10px] mt-0.5 transition-colors duration-200 tracking-wide ${
                  active ? 'text-primary font-semibold' : 'text-muted-foreground'
                }`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;
